// Collage depuis Word, Google Docs, une page web… : on ne garde que gras,
// italique, souligné, taille, surlignage et les couleurs déjà présentes dans la
// palette du document. Le reste (police, titres, liens, images) est ignoré,
// comme avec un collage en texte brut. Partagé par l'éditeur classique
// (draft-js) et le site (TipTap), qui traduisent chacun ces formats dans leurs
// propres styles.

const NAMED_COLORS = {
  black: [0, 0, 0],
  white: [255, 255, 255],
  red: [255, 0, 0],
  lime: [0, 255, 0],
  green: [0, 128, 0],
  blue: [0, 0, 255],
  yellow: [255, 255, 0],
  aqua: [0, 255, 255],
  fuchsia: [255, 0, 255],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  silver: [192, 192, 192],
  maroon: [128, 0, 0],
  olive: [128, 128, 0],
  navy: [0, 0, 128],
  purple: [128, 0, 128],
  teal: [0, 128, 128],
  orange: [255, 165, 0],
};

// Rend « r,g,b » pour une couleur CSS visible, null sinon (transparente,
// illisible). `withAlpha` ajoute l'opacité : « r,g,b,a ».
export const colorKey = (value, withAlpha = false) => {
  if (typeof value !== 'string') return null;
  const css = value.trim().toLowerCase();
  let rgb = null;
  let alpha = 1;

  const hex = css.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/);
  const fn = css.match(/^rgba?\(([^)]*)\)$/);
  if (hex) {
    const digits = hex[1].length === 3
      ? hex[1].split('').map((d) => d + d).join('')
      : hex[1];
    rgb = [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
    if (digits.length === 8) alpha = parseInt(digits.slice(6, 8), 16) / 255;
  } else if (fn) {
    const parts = fn[1].split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3) return null;
    rgb = parts.slice(0, 3).map((p) => (p.endsWith('%')
      ? Math.round(parseFloat(p) * 2.55)
      : Math.round(parseFloat(p))));
    if (parts[3] !== undefined) {
      alpha = parts[3].endsWith('%') ? parseFloat(parts[3]) / 100 : parseFloat(parts[3]);
    }
  } else if (NAMED_COLORS[css]) {
    rgb = NAMED_COLORS[css];
  }

  if (!rgb || rgb.some(Number.isNaN) || !(alpha > 0)) return null;
  return withAlpha ? [...rgb, Math.round(Math.min(alpha, 1) * 100) / 100].join(',') : rgb.join(',');
};

const BLOCK_TAGS = new Set([
  'P', 'DIV', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'BLOCKQUOTE', 'PRE', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER',
]);
const SKIPPED_TAGS = new Set(['HEAD', 'STYLE', 'SCRIPT', 'TITLE', 'META', 'XML']);

// Emoji servi en image (Gmail, Slack, X…) : son alt porte le caractère, que
// l'éditeur affiche ensuite comme ses propres emojis.
const EMOJI_ALT = /^(?:\p{Extended_Pictographic}|\p{Emoji_Component}|‍)+$/u;
const EMOJI_CHAR = /\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u;
const emojiFromImage = (img) => {
  const alt = (img.getAttribute('alt') || '').trim();
  return EMOJI_ALT.test(alt) && EMOJI_CHAR.test(alt) ? alt : null;
};

// Listes : niveau 0 à 4, comme la touche Tab de draft-js.
export const MAX_LIST_DEPTH = 4;
export const UNORDERED = 'unordered-list-item';
export const ORDERED = 'ordered-list-item';

// `mso-list` n'existe pas en CSS : le navigateur l'écarte de `element.style`,
// il faut lire l'attribut brut.
const rawStyle = (element) => (element.getAttribute('style') || '')
  .toLowerCase()
  .replace(/\s*:\s*/g, ':');

// Paragraphe de liste Word : <p style="mso-list:l0 level2 lfo1">, la puce ou le
// numéro écrit en texte dans un <span style="mso-list:Ignore">.
const wordListItem = (element) => {
  const level = /mso-list:\s*l\d+\s+level(\d+)/.exec(rawStyle(element));
  if (!level) return null;
  const marker = [...element.querySelectorAll('span')]
    .find((span) => rawStyle(span).includes('mso-list:ignore'));
  const numbered = /^[\s ]*(\d+|[a-z]|[ivxlcdm]+)[.)]/i.test(marker?.textContent || '');
  return {
    type: numbered ? ORDERED : UNORDERED,
    depth: Math.min(Number(level[1]) - 1, MAX_LIST_DEPTH),
  };
};

// Taille CSS en px (Word et Google Docs l'écrivent en pt), null pour une taille
// relative ou un mot-clé (« medium » : taille par défaut de la page source).
const fontSizePx = (value) => {
  const match = /^([\d.]+)(px|pt)$/.exec((value || '').trim().toLowerCase());
  if (!match) return null;
  const px = parseFloat(match[1]) * (match[2] === 'pt' ? 4 / 3 : 1);
  return px > 0 ? px : null;
};

const nextFormat = (element, format) => {
  const tag = element.tagName;
  const style = element.style || {};
  const next = { ...format };

  if (tag === 'B' || tag === 'STRONG') next.bold = true;
  if (tag === 'I' || tag === 'EM') next.italic = true;
  if (tag === 'U' || tag === 'INS') next.underline = true;

  // Le style en ligne l'emporte sur la balise : Google Docs enveloppe tout le
  // collage dans un <b style="font-weight:normal">.
  const weight = style.fontWeight;
  if (weight === 'bold' || weight === 'bolder' || Number(weight) >= 600) next.bold = true;
  else if (weight === 'normal' || weight === 'lighter' || Number(weight) > 0) next.bold = false;

  if (style.fontStyle === 'italic' || style.fontStyle === 'oblique') next.italic = true;
  else if (style.fontStyle === 'normal') next.italic = false;

  const decoration = style.textDecorationLine || style.textDecoration;
  if (decoration) next.underline = decoration.includes('underline');

  if (style.color) next.color = colorKey(style.color, true);

  const fontSize = fontSizePx(style.fontSize);
  if (fontSize) next.fontSize = fontSize;

  // Fond d'un élément en ligne seulement : celui d'un paragraphe ou d'une page
  // n'est pas un surlignage. Transparent (Google Docs) l'annule. `marked` :
  // Word signale ses vrais surlignages par `mso-highlight`.
  if (style.backgroundColor && !BLOCK_TAGS.has(tag)) {
    next.highlight = colorKey(style.backgroundColor);
    next.highlightMarked = /mso-highlight:(?!auto|none)/.test(rawStyle(element));
  }

  // Espaces et retours ligne gardés tels quels (Google Docs : pre-wrap partout).
  if (tag === 'PRE') next.pre = true;
  if (style.whiteSpace) next.pre = /^(pre|pre-wrap|break-spaces)$/.test(style.whiteSpace);

  return next;
};

/**
 * Le HTML du presse-papiers en paragraphes, ou null s'il n'y a rien
 * d'exploitable (on retombe alors sur le collage en texte brut).
 *
 * @returns {Array<{ text: string, formats: object[], list: { type, depth } | null }> | null}
 *   `formats` : un format par unité de `text` — `{ bold, italic, underline,
 *   color, highlight, fontSize }`, couleurs en `colorKey` (avec opacité pour
 *   le texte), taille en px.
 */
export const htmlToFormattedBlocks = (html) => {
  if (!html || typeof DOMParser === 'undefined') return null;
  // Tableau (Excel, Word) : le texte brut garde les tabulations entre cellules.
  if (/<table[\s>]/i.test(html)) return null;

  const body = new DOMParser().parseFromString(html, 'text/html').body;
  if (!body) return null;

  const blocks = [];
  // `list` : { type, depth } du bloc s'il est un élément de liste. Le texte
  // garde ses espaces insécables jusqu'à flush : ils ne se fondent pas avec
  // l'espace voisin, contrairement aux espaces ordinaires.
  let current = { text: '', formats: [], touched: false, list: null, pre: false };
  // Nombre d'éléments bloc ouverts : hors de tout bloc (fragment en ligne,
  // ex. un mot copié), les espaces de bord sont gardés.
  let blockDepth = 0;

  const flush = () => {
    let start = 0;
    let end = current.text.length;
    // Espaces de début et de fin de paragraphe, comme le rendu HTML.
    if (!current.pre && (blockDepth > 0 || current.touched)) {
      start = end - current.text.replace(/^ +/, '').length;
      end = current.text.replace(/ +$/, '').length;
    }
    const raw = current.text.trim() === '' ? '' : current.text.slice(start, Math.max(start, end));
    blocks.push({
      text: raw.replace(/ /g, ' '),
      formats: current.formats.slice(start, start + raw.length),
      list: current.list,
    });
    current = { text: '', formats: [], touched: false, list: null, pre: false };
  };

  const hasText = () => current.text.trim() !== '';

  const appendText = (raw, format, item) => {
    if (format.pre) {
      // Retour ligne d'un texte préformaté : nouveau paragraphe.
      raw.split(/\r\n|\n|\r/).forEach((line, index) => {
        if (index > 0) flush();
        current.pre = true;
        appendText(line, { ...format, pre: false, keepSpaces: true }, item);
      });
      return;
    }
    let text = format.keepSpaces ? raw : raw.replace(/[ \t\n\r\f]+/g, ' ');
    const atBlockStart = current.text === '' && (blockDepth > 0 || current.touched);
    if (!format.keepSpaces && text.startsWith(' ') && (atBlockStart || current.text.endsWith(' '))) {
      text = text.slice(1);
    }
    if (!text) return;
    if (current.text === '') current.list = item;
    const run = {
      bold: format.bold,
      italic: format.italic,
      underline: format.underline,
      color: format.color,
      highlight: format.highlight,
      highlightMarked: format.highlightMarked,
      fontSize: format.fontSize,
    };
    current.text += text;
    for (let i = 0; i < text.length; i += 1) current.formats.push(run);
  };

  // `list` : liste <ul>/<ol> englobante ({ type, depth }) ; `item` : élément de
  // liste en cours, porté par les blocs qu'il contient.
  const walk = (node, format, list = null, item = null) => {
    if (node.nodeType === 3) {
      appendText(node.nodeValue, format, item);
      return;
    }
    if (node.nodeType !== 1 || SKIPPED_TAGS.has(node.tagName)) return;
    // Puce ou numéro d'une liste Word, déjà porté par le type du bloc.
    if (rawStyle(node).includes('mso-list:ignore')) return;
    if (node.tagName === 'BR') {
      flush();
      return;
    }
    if (node.tagName === 'IMG') {
      const emoji = emojiFromImage(node);
      if (emoji) appendText(emoji, format, item);
      return;
    }
    let childList = list;
    let childItem = item;
    if (node.tagName === 'UL' || node.tagName === 'OL') {
      childList = {
        type: node.tagName === 'OL' ? ORDERED : UNORDERED,
        depth: list ? Math.min(list.depth + 1, MAX_LIST_DEPTH) : 0,
      };
    } else if (node.tagName === 'LI') {
      // Google Docs met toutes les listes au même niveau et donne le vrai
      // dans aria-level.
      const ariaLevel = Number(node.getAttribute('aria-level'));
      childItem = {
        type: childList?.type ?? UNORDERED,
        depth: ariaLevel > 0
          ? Math.min(ariaLevel - 1, MAX_LIST_DEPTH)
          : childList?.depth ?? 0,
      };
    } else {
      childItem = wordListItem(node) ?? item;
    }
    const isBlock = BLOCK_TAGS.has(node.tagName);
    if (isBlock) {
      if (hasText()) flush();
      // Espaces accumulés avant le bloc (indentation du source) : écartés.
      if (!current.pre) {
        current.text = '';
        current.formats = [];
      }
      current.touched = true;
      current.list = childItem;
      blockDepth += 1;
    }
    const childFormat = nextFormat(node, format);
    node.childNodes.forEach((child) => walk(child, childFormat, childList, childItem));
    if (isBlock) {
      if (current.touched || hasText()) flush();
      blockDepth -= 1;
    }
  };

  walk(body, {
    bold: false, italic: false, underline: false, color: null, highlight: null, highlightMarked: false, fontSize: null, pre: false,
  });
  if (hasText()) flush();

  if (!blocks.some(({ text }) => text)) return null;
  return blocks;
};

// Taille explicite égale à celle du texte : pas de taille posée, mais elle
// remplace celle du curseur.
export const DEFAULT_FONT_SIZE = 'default';

/**
 * Ce que le document garde des formats collés.
 * - `colors` : palette du document ({ label, style }) ; une couleur hors
 *   palette est ignorée. `defaultColor`, couleur CSS du texte, n'est pas
 *   reprise, le noir pur non plus : Chrome et Google Docs en marquent tout le
 *   texte copié, c'est la couleur automatique de la source.
 * - `highlightColors` : palette de surlignage ; un fond hors palette n'est
 *   repris (première couleur) que s'il est signalé comme surlignage par la
 *   source (`mso-highlight` de Word). Sinon c'est un fond de conteneur : Chrome
 *   en enveloppe tout le texte copié, il ne doit pas surligner. Le blanc est
 *   ignoré (fond de la page source).
 * - `fontSizes` : tailles disponibles en px, la plus proche est retenue ;
 *   `defaultFontSize`, celle du texte, rend DEFAULT_FONT_SIZE.
 *
 * @returns {{ color: Function, highlight: Function, fontSize: Function }}
 *   `color` et `highlight` rendent le label de la palette, ou null.
 */
export const pastePalette = ({
  colors = [],
  defaultColor = null,
  highlightColors = [],
  fontSizes = [],
  defaultFontSize = null,
} = {}) => {
  const labelByColor = {};
  colors.forEach(({ label, style }) => {
    const key = colorKey(style, true);
    if (key && !(key in labelByColor)) labelByColor[key] = label;
  });
  delete labelByColor[colorKey(defaultColor, true)];
  delete labelByColor['0,0,0,1'];

  const highlightByColor = {};
  highlightColors.forEach(({ label, style }) => {
    const key = colorKey(style);
    if (key && !(key in highlightByColor)) highlightByColor[key] = label;
  });
  const fallbackHighlight = highlightColors.find(({ style }) => colorKey(style))?.label;

  return {
    color: (key) => (key && labelByColor[key]) || null,
    highlight: (key, marked = false) => {
      if (!key || key === '255,255,255') return null;
      return highlightByColor[key] ?? (marked ? fallbackHighlight : null) ?? null;
    },
    fontSize: (px) => {
      if (!px || !fontSizes.length) return null;
      const size = fontSizes.reduce((best, value) => (
        Math.abs(value - px) < Math.abs(best - px) ? value : best
      ));
      return size === defaultFontSize ? DEFAULT_FONT_SIZE : size;
    },
  };
};
